import { Dropzone, ExtFile } from "@files-ui/react";
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/HighlightOff';
import { Box, Button, DialogActions, DialogContent, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useContext, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { ReactComponent as UploadIcon } from "../../assets/icons/upload-2.svg";
import awsExports from "../../aws-exports";
import { AuthContext } from "../../contexts/AuthContext";
import { s3UploadToExternalBucket } from "../../helpers/s3";
import useS3FileUploader from "../../hooks/useS3FileUploader";
import theme from "../../theme";
import CircularProgressPercent from "./CircularProgressPercent";

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: '24px 32px',
        height: '100%',
        width: '100%',
        overflowY: 'auto',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '24px 32px',
        backgroundColor: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
        gap: '12px'
    },
    text: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: '#666666',
    },
    uploadArea: {
        width: '100%',
        height: '100%',
        border: '2px dashed #cbd5e1 !important',
        backgroundColor: '#fff !important',
        boxShadow: 'unset !important',
        borderRadius: '12px !important',
        padding: 'unset !important',
        textAlign: 'center',
        transition: 'all 0.3s',
        '&:hover': {
            borderColor: '#14b8a6 !important',
            backgroundColor: '#f0fdfa !important',
            boxShadow: 'unset !important',
      },
    },
    uploadIconContainer: {
        width: 64,
        height: 64,
        borderRadius: '50%',
        backgroundColor: '#f0fdfa',
    },
    uploadText: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 600,
        color: '#0f172a !important',
        textTransform: 'none',
    },
    uploadTextSub: {
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 400,
        color: '#787878 !important',
        textTransform: 'none',
    },
    csvText: {
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: '#1e3a8a !important',
        textTransform: 'none',
    },
    infoBox: {
        marginTop: 8,
        padding: '16px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '12px',
    },
    closeButton: {
        width: 100,
        height: 36,
        borderRadius: 20,
        fontSize: '0.95rem',
        fontWeight: 'bold !important',
        textTransform: 'none !important' as any,
        transition: 'ease-in-out 300ms',
    },
    cancelButton: {
        color: '#475569',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '12px',
        padding: '10px 24px',
        '&:hover': {
            backgroundColor: '#f1f5f9',
        },
    },
}));

const { aws_dashboard_bulk_uploads_bucket } = awsExports;

const BULK_ADD_VALID_FILE = { maxFileSize: 100 * 1024 * 1024, allowedFileTypes: ['text/csv'], };

const BulkAddDashboards: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);

    const { uploading, progress, hasUploaded, uploadFiles, resetUploader } = useS3FileUploader(
        (extFile: ExtFile) => s3UploadToExternalBucket({
            path: ['dashboards', userGroup, `${uuidv4()}.csv`].join('/'),
            file: extFile.file,
            bucket: aws_dashboard_bulk_uploads_bucket,
        })
    );

    useEffect(() => {
        resetUploader();
    // eslint-disable-next-line
    }, []);

    return (<>
        {uploading ? (<>
            <Stack alignItems="center" justifyContent="center" width="100%" height="100%" py={10}>
                <CircularProgressPercent value={progress} />
            </Stack>
        </>) : hasUploaded ? (<>
            <DialogContent className={classes.dialogContent}>
                <Stack alignItems="center" justifyContent="center" width="100%" height="100%" py={8}>
                    <CheckIcon sx={{ fontSize: '3rem', color: theme.palette.primary.main }} />
                    <Typography variant="body1" color="black" fontWeight="bold" fontSize="1.25rem !important"
                        sx={{ marginTop: '20px !important' }}>
                        {'Saved to Notissia'}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button variant="outlined"
                    className={classes.closeButton}
                    onClick={onClose}>{'Close'}</Button>
            </DialogActions>
        </>) : (progress > 0) ? (<>
            <DialogContent className={classes.dialogContent}>
                <Stack alignItems="center" justifyContent="center" width="100%" height="100%" py={8}>
                    <CancelIcon sx={{ fontSize: '3rem', color: 'red' }}  />
                    <Typography variant="body1" color="black" fontWeight="bold" fontSize="1.25rem !important"
                        sx={{ marginTop: '20px !important' }}>
                        {'File upload error!'}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button variant="outlined"
                    className={classes.closeButton}
                    onClick={onClose}>{'Close'}</Button>
            </DialogActions>
        </>) : (<>
            <DialogContent className={classes.dialogContent}>
                <Dropzone className={classes.uploadArea}
                    onChange={uploadFiles}
                    maxFileSize={BULK_ADD_VALID_FILE.maxFileSize}
                    accept={BULK_ADD_VALID_FILE.allowedFileTypes.join(",")}
                    behaviour="replace"
                    header={false}
                    footer={false}
                    clickable={true}
                    label="">
                    <Stack direction="column" alignItems="center" justifyContent="center" width="100%" height={200}>
                        <Stack className={classes.uploadIconContainer} alignItems="center" justifyContent="center" >
                            <UploadIcon style={{ width: 32, height: 32, color: '#14b8a6' }} />
                        </Stack>
                        <Box m={1} width="100%" />
                        <Typography className={classes.uploadText} gutterBottom>
                            {'Upload CSV file'}
                        </Typography>
                        <Typography className={classes.uploadTextSub}>
                            {'Click to browse or drag and drop'}
                        </Typography>
                    </Stack>
                </Dropzone>
                <Stack spacing="4px" className={classes.infoBox}>
                    <Typography className={classes.csvText} gutterBottom>
                        {'Required CSV Format:'}
                    </Typography>
                    <Typography className={classes.csvText} style={{ fontWeight: 400, color: "#1e40af" }}>
                        {'•  First row must contain column headers'}
                    </Typography>
                    <Typography className={classes.csvText} style={{ fontWeight: 400, color: "#1e40af" }}>
                        {'•  Required columns: '}<strong>{'Name'}</strong>,{" "}<strong>{'Website'}</strong>
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button variant="text"
                    className={classes.cancelButton}
                    onClick={() => onClose()}
                > {'Cancel'} </Button>
            </DialogActions>
        </>)}
    </>);
}
export default BulkAddDashboards;